import React, { useMemo } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortfolioRiskMonitor, useRiskMonitor } from '../../services/queries/useRiskMonitor';
import type {
  PositionRiskMonitor,
  RiskLimit,
  RiskMonitor,
} from '../../services/api/riskMonitorApi';
import { Card } from '../ui/Card';

const pct = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(digits)}%`;

const money = (
  value: number | null | undefined,
  currency: string | null | undefined,
  locale: string | undefined
) => {
  if (value === null || value === undefined) return '—';
  if (!currency) return value.toLocaleString(locale);
  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
};

export function RiskLimitMonitorCard({ positionId }: { positionId: number }) {
  const { data, isLoading, notReady, error } = useRiskMonitor(positionId);

  return <RiskMonitorState data={data} isLoading={isLoading} notReady={notReady} error={error} />;
}

export function PortfolioRiskMonitorCard() {
  const { data, isLoading, notReady, error } = usePortfolioRiskMonitor();

  return <RiskMonitorState data={data} isLoading={isLoading} notReady={notReady} error={error} />;
}

const isPositionRisk = (data: PositionRiskMonitor | RiskMonitor): data is PositionRiskMonitor =>
  'position' in data;

function RiskMonitorState({
  data,
  isLoading,
  notReady,
  error,
}: {
  data: PositionRiskMonitor | RiskMonitor | undefined;
  isLoading: boolean;
  notReady: boolean;
  error: Error | undefined;
}) {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const locale = i18n.resolvedLanguage || undefined;
  const formatDate = (value: string) => new Date(value).toLocaleDateString(locale);

  if (isLoading) {
    return (
      <Card style={styles.card}>
        <ActivityIndicator color={theme.colors.primary} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={styles.card}>
        <Text style={styles.title}>
          {t('riskMonitor.title', { defaultValue: 'Risk-limit monitor' })}
        </Text>
        <Text style={styles.warning}>
          {t('riskMonitor.loadError', {
            defaultValue: 'Risk monitor could not be loaded. Please try again later.',
          })}
        </Text>
      </Card>
    );
  }

  if (notReady || !data) {
    return (
      <Card style={styles.card}>
        <Text style={styles.title}>
          {t('riskMonitor.title', { defaultValue: 'Risk-limit monitor' })}
        </Text>
        <Text style={styles.muted}>
          {t('riskMonitor.notReady', {
            defaultValue: 'No risk monitor yet — calculated in the weekly review.',
          })}
        </Text>
      </Card>
    );
  }

  if (data.degraded) {
    const reason = data.degraded_reason || 'unknown';
    return (
      <Card style={styles.card}>
        <Text style={styles.title}>
          {t('riskMonitor.degradedTitle', { defaultValue: 'Risk monitor unavailable' })}
        </Text>
        <Text style={styles.warning}>
          {t(`riskMonitor.degradedReason_${reason}`, {
            defaultValue: 'Risk measurements could not be calculated.',
          })}
        </Text>
        <Text style={styles.muted}>
          {t('riskMonitor.updated', {
            defaultValue: 'Updated {{date}}',
            date: formatDate(data.computed_at),
          })}
        </Text>
      </Card>
    );
  }

  return <RiskMonitorReport data={data} />;
}

function RiskMonitorReport({ data }: { data: PositionRiskMonitor | RiskMonitor }) {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const locale = i18n.resolvedLanguage || undefined;
  const formatDate = (value: string) => new Date(value).toLocaleDateString(locale);
  const limit = data.portfolio_limit as RiskLimit;
  const coverage = data.coverage;
  // Only the position-scoped payload carries `position`; narrow once here rather
  // than re-testing the union at every read.
  const position = isPositionRisk(data) ? data.position : undefined;

  return (
    <Card style={styles.card}>
      <Text style={styles.title}>
        {t('riskMonitor.title', { defaultValue: 'Risk-limit monitor' })}
      </Text>

      <View style={styles.metricRow}>
        <View style={styles.metricCell}>
          <Text style={styles.metricValue}>{pct(limit.current_es_used)}</Text>
          <Text style={styles.metricLabel}>
            {t('riskMonitor.currentEs', {
              defaultValue: 'Current ES (21-day, 95%)',
            })}
          </Text>
        </View>
        <View style={styles.metricCell}>
          <Text style={styles.metricValue}>{pct(limit.current_es_limit)}</Text>
          <Text style={styles.metricLabel}>
            {t('riskMonitor.limit', { defaultValue: 'Declared limit' })}
          </Text>
        </View>
      </View>

      {limit.numeric_state !== 'unknown' && (
        <Text style={styles.measurement}>
          {t('riskMonitor.exposureCeiling', {
            defaultValue: 'Exposure consistent with limit: {{value}}',
            value: pct(limit.exposure_ceiling),
          })}
        </Text>
      )}
      {limit.numeric_state === 'unknown' && (
        <Text style={styles.warning}>
          {t('riskMonitor.numericUnavailable', {
            defaultValue:
              'Not classified: {{excluded}} of position value is excluded from this estimate.',
            excluded: pct(coverage.excluded_value_fraction),
          })}
        </Text>
      )}
      {data.contribution_basis && (
        <Text style={styles.muted}>
          {t('riskMonitor.estimatorBasis', {
            defaultValue: '{{value}} basis',
            value: data.contribution_basis,
          })}
        </Text>
      )}
      {data.positions_value !== null && data.positions_value !== undefined && (
        <Text style={styles.muted}>
          {t('riskMonitor.monitoredPositionValue', {
            defaultValue: 'Monitored position value: {{value}}',
            value: money(data.positions_value, data.base_currency, locale),
          })}
        </Text>
      )}
      {data.estimation_quality && (
        <Text style={styles.muted}>
          {t('riskMonitor.tailSupport', {
            defaultValue: '{{windows}} tail windows · {{episodes}} effective tail episodes',
            windows: data.estimation_quality.tail_windows ?? '—',
            episodes: data.estimation_quality.effective_tail_episodes ?? '—',
          })}
        </Text>
      )}
      {data.as_of && (
        <Text style={styles.muted}>
          {t('riskMonitor.priceAsOf', {
            defaultValue: 'Price data through {{date}}.',
            date: new Date(data.as_of).toLocaleDateString(locale),
          })}
        </Text>
      )}
      {limit.current_es_compounded_display !== null &&
        limit.current_es_compounded_display !== undefined && (
          <Text style={styles.muted}>
            {t('riskMonitor.compoundedEstimate', {
              defaultValue: 'Compounded-loss estimate: {{value}}',
              value: pct(limit.current_es_compounded_display),
            })}
          </Text>
        )}

      {data.theme_limits.state === 'calibration_pending' && (
        <Text style={styles.muted}>
          {t('riskMonitor.themeCalibrationPending', {
            defaultValue:
              'Theme limits pending calibration; concentration is shown for information.',
          })}
        </Text>
      )}

      <Text style={styles.sectionTitle}>
        {t('riskMonitor.themeTitle', {
          defaultValue: 'Signed share of ES',
        })}
      </Text>
      {data.theme_limits.themes.map((theme) => (
        <View key={theme.theme} style={styles.themeRow}>
          <Text
            style={[
              theme.signed_net_share !== null && theme.signed_net_share < 0
                ? styles.hedge
                : styles.themeName,
            ]}
          >
            {theme.theme}
          </Text>
          <Text style={styles.themeValue}>
            {pct(theme.signed_net_share)} · {pct(theme.contribution_pp)}
          </Text>
        </View>
      ))}

      {(coverage.fx_state === 'fx_unadjusted' || coverage.fx_immaterial === false) && (
        <Text style={styles.warning}>
          {t('riskMonitor.fxCaveat', {
            defaultValue:
              'FX-unadjusted: {{value}} of position value is outside your base currency.',
            value: pct(coverage.fx_unadjusted_value_fraction),
          })}
        </Text>
      )}
      {(coverage.state === 'coverage_limited' ||
        coverage.excluded_state === 'coverage_limited') && (
        <Text style={styles.warning}>
          {t('riskMonitor.coverage', {
            defaultValue:
              'Coverage-limited: {{value}} of position value is excluded from this estimate.',
            value: pct(coverage.excluded_value_fraction),
          })}
        </Text>
      )}
      {coverage.limit_scope && coverage.limit_scope !== 'whole_book' && (
        <Text style={styles.muted}>
          {t('riskMonitor.coverageScope', {
            defaultValue: 'Estimate scope: {{value}}.',
            value: coverage.limit_scope.replaceAll('_', ' '),
          })}
        </Text>
      )}

      {position && (
        <>
          <Text style={styles.sectionTitle}>
            {t('riskMonitor.positionTitle', { defaultValue: 'This position' })}
          </Text>
          <Text style={styles.measurement}>
            {position.symbol} ·{' '}
            {t('riskMonitor.positionShare', {
              defaultValue: '{{value}} of ES',
              value: pct(position.signed_net_share),
            })}
          </Text>
          {position.current_weight !== null && position.current_weight !== undefined && (
            <Text style={styles.muted}>
              {t('riskMonitor.positionWeight', {
                defaultValue: 'Current weight: {{value}}',
                value: pct(position.current_weight),
              })}
            </Text>
          )}
        </>
      )}

      {data.position_policy.state === 'breach' &&
        data.position_policy.breaches?.map((breach) => (
          <Text key={`${breach.symbol}-${breach.current_weight}`} style={styles.warning}>
            {t('riskMonitor.policyBreach', {
              defaultValue:
                '{{symbol}} is {{current}} versus your {{limit}} single-position policy.',
              symbol: breach.symbol,
              current: pct(breach.current_weight),
              limit: pct(breach.limit),
            })}
          </Text>
        ))}

      <Text style={styles.muted}>
        {t('riskMonitor.updated', {
          defaultValue: 'Updated {{date}}',
          date: formatDate(data.computed_at),
        })}
      </Text>
    </Card>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      marginHorizontal: theme.spacing.md,
      marginTop: theme.spacing.sm,
      padding: theme.spacing.md,
    },
    title: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      marginBottom: theme.spacing.md,
    },
    metricRow: { flexDirection: 'row', gap: theme.spacing.xl },
    metricCell: { flex: 1 },
    metricValue: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
    },
    metricLabel: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
    },
    measurement: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      marginTop: theme.spacing.sm,
    },
    muted: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
    },
    warning: {
      color: theme.colors.warning,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xs,
    },
    sectionTitle: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.xs,
    },
    themeRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.xxs,
    },
    themeName: { color: theme.colors.text, fontSize: theme.typography.fontSizeSm },
    hedge: {
      color: theme.colors.success,
      fontSize: theme.typography.fontSizeSm,
    },
    themeValue: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
